/**
 * "I Miei File" — system upload area for contacts.
 *
 * Two sections:
 * 1. Month slider (Mese di Competenza) — horizontal card carousel of months,
 *    click to open a "cabinet drawer" for that month, upload goes there.
 * 2. Category selector + drag/drop upload zone
 * 3. File list filtered by selected month
 */

import React, { useState, useEffect, useRef, useMemo } from "react";
import { useApiUrl } from "@refinedev/core";
import axios from "axios";
import { VaultDropZone } from "@/components/digibox/VaultDropZone";
import { useDocumentUpload } from "@/hooks/useDocumentUpload";
import { usePortalContact } from "@/hooks/usePortalContact";
import { useDownloadTracker } from "@/hooks/useDownloadTracker";

const CATEGORIES = [
  { key: "risposta", label: "Risposta" },
  { key: "identita", label: "Documento Identità" },
  { key: "contratto", label: "Contratto Firmato" },
  { key: "fiscale", label: "Documento Fiscale" },
  { key: "altro", label: "Altro" },
];

const MONTH_NAMES = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];

/** Extract YYYY-MM from a fiscal_date or createdtime field */
function toMonthKey(dateStr?: string): string | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function parseCategory(desc?: string): string | null {
  const m = (desc || "").match(/\[CAT:([^\]]+)\]/);
  return m ? m[1] : null;
}

interface UploadedFile {
  id: string;
  notes_title: string;
  filename: string;
  filetype: string;
  filesize: string;
  createdtime: string;
  notecontent?: string;
  filedescription?: string;
  fiscal_date?: string;
}

export const UploadArea: React.FC = () => {
  const apiUrl = useApiUrl();
  const { contactId } = usePortalContact();
  const { upload, uploading, error } = useDocumentUpload();
  const { isDownloaded, markDownloaded } = useDownloadTracker();
  const [category, setCategory] = useState("altro");
  const [myFiles, setMyFiles] = useState<UploadedFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadQueue, setUploadQueue] = useState<{ file: File; status: "pending" | "done" | "error" }[]>([]);

  // Month slider state
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth(); // 0-indexed
  const [selectedMonth, setSelectedMonth] = useState<string>(
    `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}`
  );
  const sliderRef = useRef<HTMLDivElement>(null);

  // Generate 24 months: 12 back + current + 11 forward
  const months = useMemo(() => {
    const result: { key: string; label: string; year: number; month: number }[] = [];
    for (let offset = -12; offset <= 11; offset++) {
      const d = new Date(currentYear, currentMonth + offset, 1);
      const y = d.getFullYear();
      const m = d.getMonth();
      result.push({
        key: `${y}-${String(m + 1).padStart(2, "0")}`,
        label: `${MONTH_NAMES[m]} ${y}`,
        year: y,
        month: m,
      });
    }
    return result;
  }, [currentYear, currentMonth]);

  // Scroll to current month on mount
  useEffect(() => {
    if (sliderRef.current) {
      const activeCard = sliderRef.current.querySelector("[data-active=\"true\"]");
      if (activeCard) {
        activeCard.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
      }
    }
  }, []);

  // Load contact's documents
  useEffect(() => {
    if (!contactId) { setLoading(false); return; }
    const fetchFiles = async () => {
      try {
        const res = await axios.post(`${apiUrl}/direct/get-related`, {
          id: contactId,
          module: "Contacts",
          related_module: "Documents",
          query_params: { limit: 500, orderby: "createdtime DESC" },
        });
        if (res.data.success) {
          const raw = res.data.result?.records ?? res.data.result;
          setMyFiles(Array.isArray(raw) ? raw : Object.values(raw || {}));
        }
      } catch { /* */ }
      setLoading(false);
    };
    fetchFiles();
  }, [contactId, apiUrl]);

  // Resolve the month key for a file: fiscal_date > createdtime
  const fileMonthKey = (f: UploadedFile): string | null =>
    toMonthKey(f.fiscal_date) || toMonthKey(f.createdtime);

  const filesForMonth = useMemo(() =>
    myFiles.filter((f) => fileMonthKey(f) === selectedMonth),
  [myFiles, selectedMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  const countByMonth = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const f of myFiles) {
      const key = fileMonthKey(f);
      if (key) counts[key] = (counts[key] || 0) + 1;
    }
    return counts;
  }, [myFiles]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFiles = async (files: File[]) => {
    const queue = files.map((f) => ({ file: f, status: "pending" as const }));
    setUploadQueue((prev) => [...prev, ...queue]);

    const fiscalDate = `${selectedMonth}-01`; // first day of selected month
    for (const file of files) {
      const desc = `[CAT:${category}]`;
      const result = await upload(file, { description: desc, category, fiscalDate });
      setUploadQueue((prev) =>
        prev.map((item) =>
          item.file === file ? { ...item, status: result ? "done" : "error" } : item
        )
      );
      if (result) {
        setMyFiles((prev) => [{
          id: result.id,
          notes_title: result.filename,
          filename: result.filename,
          filetype: result.filetype,
          filesize: String(result.filesize),
          createdtime: new Date().toISOString(),
          filedescription: desc,
          fiscal_date: fiscalDate,
        }, ...prev]);
      }
    }
    setTimeout(() => setUploadQueue((prev) => prev.filter((item) => item.status === "pending")), 3000);
  };

  const handleDownload = async (doc: UploadedFile) => {
    try {
      const res = await axios.post(`${apiUrl}/direct/retrieve-attachment`, {
        doc_ids: [doc.id], return_file: true,
      });
      if (res.data.success && res.data.result) {
        const fd = res.data.result[doc.id] || Object.values(res.data.result)[0] as any;
        const base64 = fd?.filecontent || fd?.attachment;
        if (base64) {
          const fname = fd.filename || doc.filename || "download";
          const link = document.createElement("a");
          link.href = `data:${fd.filetype || "application/octet-stream"};base64,${base64}`;
          link.download = fname;
          link.click();
          markDownloaded(doc.id, fname);
        }
      }
    } catch { /* */ }
  };

  const formatSize = (s: string | number) => {
    const n = Number(s); if (!n) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (d: string) =>
    d ? new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  const selectedLabel = months.find((m) => m.key === selectedMonth)?.label || selectedMonth;

  return (
    <main className="vault-panel vault-main-content" style={{ gridColumn: "1 / -1" }}>
      <div className="vault-hub-content">
        <div className="vault-hub-hero">
          <h1 className="vault-heading-display">I MIEI<br />FILE</h1>
        </div>

        {/* ── Month slider ──────────────────────────── */}
        <div>
          <span className="vault-label-bracketed" style={{ display: "block", marginBottom: "var(--vault-spacing-xs)" }}>
            Mese di Competenza
          </span>
          <div
            ref={sliderRef}
            style={{
              display: "flex",
              gap: "1px",
              overflowX: "auto",
              scrollBehavior: "smooth",
              scrollbarWidth: "none",
              background: "var(--vault-grid-line)",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {months.map((m) => {
              const isActive = m.key === selectedMonth;
              const isCurrent = m.year === currentYear && m.month === currentMonth;
              const count = countByMonth[m.key] || 0;
              return (
                <div
                  key={m.key}
                  data-active={isActive ? "true" : "false"}
                  onClick={() => setSelectedMonth(m.key)}
                  style={{
                    minWidth: 110,
                    padding: "var(--vault-spacing-xs) var(--vault-spacing-sm)",
                    background: isActive ? "var(--vault-zone-upload-bg)" : "var(--vault-bg)",
                    borderLeft: isActive ? "3px solid var(--vault-zone-upload-border)" : "3px solid transparent",
                    cursor: "pointer",
                    transition: "all 0.15s",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <span className="vault-label-tiny" style={{
                    fontWeight: isActive || isCurrent ? 600 : 400,
                    color: isActive ? "var(--vault-zone-upload-border)" : isCurrent ? "var(--vault-accent)" : undefined,
                  }}>
                    {MONTH_NAMES[m.month].slice(0, 3).toUpperCase()}
                  </span>
                  <span className="vault-text-small" style={{ fontWeight: isActive ? 500 : 300 }}>
                    {m.year}
                  </span>
                  {count > 0 && (
                    <span style={{
                      fontSize: "0.55rem",
                      background: isActive ? "var(--vault-zone-upload-border)" : "var(--vault-grid-line-strong)",
                      color: isActive ? "#fff" : "var(--vault-fg-muted)",
                      padding: "0 5px",
                      borderRadius: 2,
                      alignSelf: "flex-start",
                      fontWeight: 600,
                    }}>
                      {count}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Open cabinet drawer ──────────────────── */}
        <div style={{
          borderLeft: "3px solid var(--vault-zone-upload-border)",
          background: "var(--vault-zone-upload-bg)",
          padding: "var(--vault-spacing-md)",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "var(--vault-spacing-sm)" }}>
            <span className="vault-heading-editorial" style={{ fontSize: "var(--vault-text-h3)" }}>
              {selectedLabel}
            </span>
            <span className="vault-label-tiny">
              {filesForMonth.length} document{filesForMonth.length !== 1 ? "i" : "o"}
            </span>
          </div>

          {/* Category + upload */}
          <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: "var(--vault-spacing-sm)", alignItems: "start", marginBottom: "var(--vault-spacing-sm)" }}>
            <div>
              <span className="vault-label-tiny" style={{ display: "block", marginBottom: 4 }}>Categoria</span>
              {CATEGORIES.map((cat) => (
                <span
                  key={cat.key}
                  className={`vault-nav-link ${category === cat.key ? "active" : ""}`}
                  onClick={() => setCategory(cat.key)}
                  style={{ display: "block", padding: "3px 0", cursor: "pointer", fontSize: "var(--vault-text-tiny)" }}
                >
                  {cat.label}
                </span>
              ))}
            </div>
            <VaultDropZone onFiles={handleFiles} disabled={uploading} />
          </div>

          {/* Upload queue */}
          {uploadQueue.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", marginBottom: "var(--vault-spacing-sm)" }}>
              {uploadQueue.map((item, i) => (
                <div
                  key={i}
                  className={item.status === "done" ? "vault-zone-accepted" : item.status === "error" ? "vault-zone-download" : "vault-zone-upload"}
                  style={{ display: "flex", justifyContent: "space-between", padding: "var(--vault-spacing-xs) var(--vault-spacing-sm)" }}
                >
                  <span className="vault-text-small">{item.file.name}</span>
                  <span className="vault-label-tiny">
                    {item.status === "pending" ? "Caricamento..." : item.status === "done" ? "✓ Caricato" : "✕ Errore"}
                  </span>
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="vault-zone-download" style={{ padding: "var(--vault-spacing-xs) var(--vault-spacing-sm)", marginBottom: "var(--vault-spacing-sm)" }}>
              <span className="vault-text-small">{error}</span>
            </div>
          )}

          {/* Files for this month */}
          {loading ? (
            <div className="vault-loading">Loading...</div>
          ) : filesForMonth.length === 0 ? (
            <div style={{ textAlign: "center", padding: "var(--vault-spacing-md)", color: "var(--vault-fg-light)" }}>
              <span className="vault-label-tiny">Nessun documento per {selectedLabel}</span>
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--vault-grid-line-strong)" }}>
                  <th className="vault-label-tiny" style={{ textAlign: "left", padding: "var(--vault-spacing-xs) 0", fontWeight: 500 }}>Nome</th>
                  <th className="vault-label-tiny" style={{ textAlign: "left", padding: "var(--vault-spacing-xs) 0", fontWeight: 500, width: 90 }}>Tipo</th>
                  <th className="vault-label-tiny" style={{ textAlign: "left", padding: "var(--vault-spacing-xs) 0", fontWeight: 500, width: 90 }}>Categoria</th>
                  <th className="vault-label-tiny" style={{ textAlign: "right", padding: "var(--vault-spacing-xs) 0", fontWeight: 500, width: 70 }}>Dim.</th>
                  <th className="vault-label-tiny" style={{ textAlign: "right", padding: "var(--vault-spacing-xs) 0", fontWeight: 500, width: 100 }}>Data</th>
                  <th style={{ width: 40 }} />
                </tr>
              </thead>
              <tbody>
                {filesForMonth.map((d) => (
                  <tr
                    key={d.id}
                    style={{ borderBottom: "1px solid var(--vault-grid-line)", cursor: "pointer" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(124,58,237,0.04)")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "")}
                    onClick={() => handleDownload(d)}
                  >
                    <td style={{ padding: "var(--vault-spacing-xs) 0" }}>
                      <span className="vault-text-small" style={{ fontWeight: 500 }}>
                        {d.notes_title || d.filename || "Documento"}
                      </span>
                      {isDownloaded(d.id) && (
                        <span style={{ display: "inline-block", background: "var(--vault-zone-accepted-border)", color: "#fff", fontSize: "0.5rem", padding: "1px 5px", borderRadius: 2, marginLeft: 6, fontWeight: 600 }}>
                          ✓ SCARICATO
                        </span>
                      )}
                    </td>
                    <td className="vault-text-muted" style={{ padding: "var(--vault-spacing-xs) 0", fontSize: "var(--vault-text-tiny)", textTransform: "uppercase" }}>
                      {(d.filetype || "").replace("application/", "").replace("image/", "").split(";")[0]}
                    </td>
                    <td className="vault-text-muted" style={{ padding: "var(--vault-spacing-xs) 0", fontSize: "var(--vault-text-tiny)" }}>
                      {parseCategory(d.filedescription || d.notecontent) || "—"}
                    </td>
                    <td className="vault-text-muted" style={{ padding: "var(--vault-spacing-xs) 0", fontSize: "var(--vault-text-tiny)", textAlign: "right" }}>
                      {formatSize(d.filesize)}
                    </td>
                    <td className="vault-text-muted" style={{ padding: "var(--vault-spacing-xs) 0", fontSize: "var(--vault-text-tiny)", textAlign: "right" }}>
                      {formatDate(d.createdtime)}
                    </td>
                    <td style={{ padding: "var(--vault-spacing-xs) 0", textAlign: "right" }}>
                      <button className="vault-btn-ghost" onClick={(e) => { e.stopPropagation(); handleDownload(d); }}
                        style={{ fontSize: "var(--vault-text-tiny)", padding: "4px 8px" }}>↓</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </main>
  );
};
